// ============================================================================
// New supply request — multi-item modal. One facility, N line items, voice
// capture that parses several items from a single transcript.
//
// Shape:
//   envelope (facility, default urgency, date needed, notes)
//   + lines: Array<{ supply | itemDescription, quantity, unit, urgency }>
//
// On Submit:
//   crypto.randomUUID() => request_batch_id, stamped on every line.
//   Each line is inserted as a separate supply_requests row with its own
//   per-line status (deriveStatus runs per row, exec items still escalate
//   to 'Submitted').
//
// Voice capture flows through parseTranscript() => ParsedSupplyBatch and
// replaces the envelope + lines. The AM can edit anything before review.
// "a case of X" pre-fills qty=1 + unit=CASE; "some X" leaves them blank and
// the Review button stays disabled until every line has positive qty + unit.
//
// Approval is still per-row. The approval queue groups by request_batch_id
// for display only.
// ============================================================================

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useAuth } from '../../shared/auth';
import { supabase } from '../../shared/supabase';
import type {
  Facility,
  Supply,
  SupplyRequestStatus,
  SupplyUrgency,
} from '../../shared/types';
import FacilityPicker from './facility-picker';
import SupplyPicker from './supply-picker';
import {
  isVoiceSupported,
  parseTranscript,
  useVoiceRecognition,
} from './voice-capture';
import { overlayClickClose } from '../../shared/overlay-close';
import { FormExplainer } from '../../shared/explainer';
import { errorMessage } from '../../shared/errors';

interface Props {
  onClose: () => void;
  onSaved: () => void;
}

interface LineState {
  id: string; // client-side React key (NOT the batch_id)
  supply: Supply | null;
  itemDescription: string; // free-text fallback when supply is null
  quantity: string; // keep as string so the controlled input stays clean
  unit: string;
  urgency: SupplyUrgency; // initialized to envelope.urgencyDefault on add
}

interface FormState {
  facility: Facility | null;
  urgencyDefault: SupplyUrgency;
  dateNeeded: string; // ISO date from <input type="date">, '' when blank
  notes: string;
  lines: LineState[];
}

const URGENCY_OPTIONS: {
  value: SupplyUrgency;
  label: string;
  hint: string;
  color: string;
}[] = [
  {
    value: 'Emergency',
    label: 'Emergency',
    hint: 'Same / next day',
    color: 'bg-st-danger-solid text-st-solid-ink',
  },
  {
    value: 'Soon',
    label: 'Soon',
    hint: 'Within 5 days',
    color: 'bg-st-warn-solid text-st-solid-ink',
  },
  {
    value: 'Planned',
    label: 'Planned',
    hint: 'Up to 2 weeks',
    color: 'bg-st-ok-solid text-st-solid-ink',
  },
];

const URGENCY_TONE: Record<SupplyUrgency, 'red' | 'amber' | 'green'> = {
  Emergency: 'red',
  Soon: 'amber',
  Planned: 'green',
};

// ---------------------------------------------------------------------------
// Status helpers — applied per line, not per envelope.
// ---------------------------------------------------------------------------

// Heuristic for the "Equipment category requires COO approval" rule. We can't
// trust Equipment supplies to all live in supply_category=='EQP' (some live in
// USE/CHM with is_capital_equipment='Yes'), so we check both.
function needsExecApproval(supply: Supply | null): boolean {
  if (!supply) return false;
  if (supply.supply_category === 'EQP') return true;
  if (
    supply.is_capital_equipment &&
    supply.is_capital_equipment.toLowerCase().startsWith('y')
  )
    return true;
  return false;
}

function deriveStatus(
  supply: Supply | null,
  urgency: SupplyUrgency,
  estCost: number | null,
): SupplyRequestStatus {
  // Per-row routing. Items needing exec approval (Equipment, capital) and
  // Emergency >$500 await an approver — they stay at 'Submitted'. Everything
  // else self-approves and clears straight to 'Approved'.
  if (needsExecApproval(supply)) return 'Submitted';
  if (urgency === 'Emergency' && (estCost ?? 0) > 500) return 'Submitted';
  return 'Approved';
}

function lineEstimatedCost(line: LineState): number | null {
  const q = Number(line.quantity);
  if (!Number.isFinite(q) || q <= 0) return null;
  if (line.supply?.unit_cost != null) return q * line.supply.unit_cost;
  return null;
}

// Stable client-side ID for React keys. crypto.randomUUID needs a secure
// context — production is HTTPS, dev runs on localhost, both qualify.
function newLineId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `line-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

function emptyLine(urgency: SupplyUrgency): LineState {
  return {
    id: newLineId(),
    supply: null,
    itemDescription: '',
    quantity: '',
    unit: '',
    urgency,
  };
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function NewSupplyRequest({ onClose, onSaved }: Props) {
  const { employee } = useAuth();
  const [step, setStep] = useState<'capture' | 'confirm'>('capture');
  const [submitting, setSubmitting] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parserNote, setParserNote] = useState<string | null>(null);
  const voice = useVoiceRecognition();

  const [form, setForm] = useState<FormState>(() => ({
    facility: null,
    urgencyDefault: 'Soon',
    dateNeeded: '',
    notes: '',
    lines: [emptyLine('Soon')],
  }));

  // Voice -> AI parse -> prefill envelope + replace lines. Triggered when the
  // recognizer finalizes a transcript; the AM can edit anything afterward.
  useEffect(() => {
    if (!voice.transcript || parsing) return;
    let cancelled = false;
    (async () => {
      setParsing(true);
      setParserNote(null);
      try {
        // Pull lightweight catalog/facility lists for the parser context.
        // RLS scopes facilities to whoever can read them.
        const [{ data: supplies }, { data: facilities }] = await Promise.all([
          supabase
            .from('supplies')
            .select('supply_id, item_name, supply_category')
            .order('item_name'),
          // 2026-05-12 repoint: voice-parser context — supply orders go
          // only to active facilities. Use the active_facilities view.
          supabase
            .from('active_facilities')
            .select('facility_id, facility_name')
            .order('facility_name'),
        ]);

        const batch = await parseTranscript(voice.transcript, {
          supplies: (supplies ?? []) as Pick<
            Supply,
            'supply_id' | 'item_name' | 'supply_category'
          >[],
          facilities: (facilities ?? []) as Pick<
            Facility,
            'facility_id' | 'facility_name'
          >[],
        });
        if (cancelled) return;

        if (!batch) {
          // Network / proxy failure. Drop the transcript into the first
          // line's description so the AM can recover.
          setParserNote(
            "Couldn't parse that. The transcript is in the first item — review and submit, or speak again.",
          );
          setForm((prev) => {
            const first =
              prev.lines[0] ?? emptyLine(prev.urgencyDefault);
            const rest = prev.lines.slice(1);
            return {
              ...prev,
              lines: [
                { ...first, itemDescription: voice.transcript },
                ...rest,
              ],
            };
          });
          return;
        }

        // Resolve header.facility_id back to a full Facility row so the
        // picker displays it.
        let facilityMatch: Facility | null = null;
        if (batch.header.facility_id) {
          const { data } = await supabase
            .from('active_facilities')
            .select(
              // 2026-05-19: denorm counter cols (last_cleaning_date, last_feedback_date,
              // open_task_count, open_feedback_count) dropped from facilities.
              'facility_id, facility_name, account_id, facility_type, facility_address, state, status, market_region, account_manager_eid, account_manager_name, monthly_rate, account_status_rag, rag_set_by, rag_set_at, rag_reason, rag_category, latitude, longitude',
            )
            .eq('facility_id', batch.header.facility_id)
            .maybeSingle();
          facilityMatch = (data as Facility | null) ?? null;
        }

        // Resolve each item's supply_id to a full Supply row in parallel.
        // We hit supplies in a single IN-lookup, then map back per item.
        const wantedSupplyIds = Array.from(
          new Set(
            batch.items
              .map((it) => it.supply_id)
              .filter((v): v is string => !!v),
          ),
        );
        const supplyMap = new Map<string, Supply>();
        if (wantedSupplyIds.length > 0) {
          const { data } = await supabase
            .from('supplies')
            .select(
              'supply_id, item_name, sku, supply_category, sub_category, preferred_vendor, vendor_id, unit, unit_cost, reimbursable, is_capital_equipment',
            )
            .in('supply_id', wantedSupplyIds);
          for (const s of (data ?? []) as Supply[]) {
            supplyMap.set(s.supply_id, s);
          }
        }

        const envelopeUrgency = batch.header.urgency ?? form.urgencyDefault;

        // Build lines. If the parser returned no items, fall back to a single
        // blank line carrying the transcript in itemDescription.
        const newLines: LineState[] =
          batch.items.length === 0
            ? [
                {
                  ...emptyLine(envelopeUrgency),
                  itemDescription: voice.transcript,
                },
              ]
            : batch.items.map((it) => {
                const supplyMatch =
                  (it.supply_id && supplyMap.get(it.supply_id)) || null;
                return {
                  id: newLineId(),
                  supply: supplyMatch,
                  itemDescription:
                    supplyMatch?.item_name ?? it.item ?? '',
                  quantity:
                    it.quantity != null ? String(it.quantity) : '',
                  unit: (it.unit ?? supplyMatch?.unit ?? '').toUpperCase(),
                  urgency: envelopeUrgency,
                };
              });

        setForm((prev) => ({
          ...prev,
          facility: facilityMatch ?? prev.facility,
          urgencyDefault: envelopeUrgency,
          dateNeeded: batch.header.date_needed ?? prev.dateNeeded,
          notes: batch.header.notes ?? prev.notes,
          lines: newLines,
        }));

        const blankCount = newLines.filter(
          (l) => !l.quantity || !l.unit,
        ).length;
        setParserNote(
          blankCount > 0
            ? `Parsed ${newLines.length} item${newLines.length === 1 ? '' : 's'}. ${blankCount} need${blankCount === 1 ? 's' : ''} a quantity / unit — fill those in before submitting.`
            : `Parsed ${newLines.length} item${newLines.length === 1 ? '' : 's'}. Review the fields below and edit anything that looks off.`,
        );
      } finally {
        if (!cancelled) setParsing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // We intentionally only re-run when transcript changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice.transcript]);

  // -------------------------------------------------------------------------
  // Derived state
  // -------------------------------------------------------------------------

  const estimatedTotal = useMemo(() => {
    let total = 0;
    let any = false;
    for (const line of form.lines) {
      const c = lineEstimatedCost(line);
      if (c != null) {
        total += c;
        any = true;
      }
    }
    return any ? total : null;
  }, [form.lines]);

  function readyForConfirm(): string | null {
    if (!form.facility) return 'Select a facility.';
    if (form.lines.length === 0) return 'Add at least one supply line.';
    for (let i = 0; i < form.lines.length; i++) {
      const line = form.lines[i];
      const itemOk =
        (line.supply && line.supply.item_name) ||
        line.itemDescription.trim().length > 0;
      if (!itemOk) return `Line ${i + 1}: pick a supply or describe what you need.`;
      const q = Number(line.quantity);
      if (!Number.isFinite(q) || q <= 0)
        return `Line ${i + 1}: enter a quantity greater than zero.`;
      if (!line.unit.trim())
        return `Line ${i + 1}: specify a unit (CASE, EACH, …).`;
    }
    return null;
  }
  const canConfirm = readyForConfirm() === null;

  // -------------------------------------------------------------------------
  // Mutators
  // -------------------------------------------------------------------------

  function updateLine(id: string, patch: Partial<LineState>) {
    setForm((p) => ({
      ...p,
      lines: p.lines.map((l) => (l.id === id ? { ...l, ...patch } : l)),
    }));
  }

  function removeLine(id: string) {
    setForm((p) => {
      const next = p.lines.filter((l) => l.id !== id);
      // Never end up with zero lines — keep one blank so the AM always has
      // somewhere to type.
      return {
        ...p,
        lines: next.length === 0 ? [emptyLine(p.urgencyDefault)] : next,
      };
    });
  }

  function addLine() {
    setForm((p) => ({
      ...p,
      lines: [...p.lines, emptyLine(p.urgencyDefault)],
    }));
  }

  function setEnvelopeUrgency(u: SupplyUrgency) {
    setForm((p) => ({ ...p, urgencyDefault: u }));
  }

  function applyEnvelopeUrgencyToAll() {
    setForm((p) => ({
      ...p,
      lines: p.lines.map((l) => ({ ...l, urgency: p.urgencyDefault })),
    }));
  }

  const anyLineDiffersFromEnvelope = form.lines.some(
    (l) => l.urgency !== form.urgencyDefault,
  );

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!employee?.employee_id) {
      setError('No employee record on session — sign out and back in.');
      return;
    }
    if (!form.facility) {
      setError('Select a facility before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      // Single client-side UUID groups every row in this submission. Even
      // single-line submissions get a batch_id going forward — historical
      // rows keep NULL.
      const batchId =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `batch-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const rows = form.lines.map((line) => {
        const itemDescription =
          line.supply?.item_name?.trim() || line.itemDescription.trim();
        const estCost = lineEstimatedCost(line);
        const status = deriveStatus(line.supply, line.urgency, estCost);
        return {
          request_batch_id: batchId,
          facility_id: form.facility!.facility_id,
          account_id: form.facility!.account_id,
          supply_id: line.supply?.supply_id ?? null,
          item_description: itemDescription,
          quantity: Number(line.quantity),
          unit_of_measure: line.unit.trim().toUpperCase(),
          urgency: line.urgency,
          date_needed: form.dateNeeded || null,
          requested_by: employee.employee_id,
          approved_by:
            status === 'Approved' ? employee.employee_id : null,
          approved_at:
            status === 'Approved' ? new Date().toISOString() : null,
          ship_to_address: form.facility!.facility_address ?? null,
          reimbursable: line.supply?.reimbursable
            ? line.supply.reimbursable.toLowerCase().startsWith('y')
            : false,
          notes: form.notes.trim() || null,
          status,
          estimated_cost: estCost,
        };
      });

      const { error: insErr } = await supabase
        .from('supply_requests')
        .insert(rows);
      if (insErr) throw insErr;
      onSaved();
    } catch (e) {
      console.error('[new-request] insert failed', e);
      setError(errorMessage(e, 'Could not save request.'));
    } finally {
      setSubmitting(false);
    }
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-tcs-scrim/60 sm:items-center"
      {...overlayClickClose(onClose)}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-xl bg-tcs-overlay p-5 shadow-xl sm:rounded-xl"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-tcs-text">
              {step === 'capture'
                ? 'New supply request'
                : `Review request — ${form.lines.length} item${form.lines.length === 1 ? '' : 's'}`}
            </h2>
            <p className="mt-1 text-xs text-tcs-text-muted">
              {step === 'capture'
                ? 'Tap the mic and speak, or fill the fields manually. Add as many items as you need.'
                : 'Confirm the details before submitting.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-tcs-border px-2 py-1 text-xs text-tcs-text-secondary hover:bg-tcs-bg-muted"
          >
            Close
          </button>
        </div>

        {/* 2026-07-06 — collapsible "how this works" explainer (DB-backed). */}
        <div className="mt-3">
          <FormExplainer surfaceKey="form:supply-request" />
        </div>

        {step === 'capture' && (
          <>
            {/* Voice block */}
            {isVoiceSupported() && (
              <div className="mt-4 rounded-lg border border-tcs-border bg-tcs-bg-muted p-3">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={voice.isListening ? voice.stop : voice.start}
                    aria-label={voice.isListening ? 'Stop listening' : 'Start voice input'}
                    className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-2xl text-tcs-inverse-ink shadow-md transition ${
                      voice.isListening
                        ? 'animate-pulse bg-rag-red'
                        : 'bg-tcs-inverse hover:bg-tcs-inverse-hover'
                    }`}
                  >
                    {voice.isListening ? '■' : '🎙'}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-tcs-text">
                      {voice.isListening
                        ? 'Listening…'
                        : parsing
                          ? 'Parsing…'
                          : voice.transcript
                            ? 'Transcript captured'
                            : 'Tap to speak'}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-tcs-text-muted">
                      {voice.transcript ||
                        'e.g. "I need a case of virex, a case of glance, and some toilet bowl cleaner at KCUC by Friday"'}
                    </div>
                  </div>
                </div>
                {voice.error && (
                  <p className="mt-2 text-xs text-rag-red">{voice.error}</p>
                )}
                {parserNote && (
                  <p className="mt-2 rounded bg-st-info-bg px-2 py-1 text-xs text-tcs-accent-ink">
                    {parserNote}
                  </p>
                )}
              </div>
            )}
            {!isVoiceSupported() && (
              <p className="mt-3 rounded-md border border-st-warn-border bg-st-warn-bg px-3 py-2 text-xs text-st-warn-fg-strong">
                Voice input isn't supported in this browser. Use the form below.
              </p>
            )}

            {/* Envelope */}
            <div className="mt-4 space-y-4">
              <Field label="Where? (Facility)">
                <FacilityPicker
                  value={form.facility?.facility_id ?? null}
                  onChange={(f) =>
                    setForm((p) => ({ ...p, facility: f }))
                  }
                />
              </Field>

              <Field label="Default urgency (applies to new lines)">
                <div className="grid grid-cols-3 gap-2">
                  {URGENCY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setEnvelopeUrgency(opt.value)}
                      className={`rounded-md border px-2 py-3 text-sm transition ${
                        form.urgencyDefault === opt.value
                          ? `${opt.color} border-transparent shadow-sm`
                          : 'border-tcs-border bg-tcs-raised text-tcs-text-secondary hover:bg-tcs-bg-muted'
                      }`}
                    >
                      <div className="font-semibold">{opt.label}</div>
                      <div className="text-[10px] opacity-90">{opt.hint}</div>
                    </button>
                  ))}
                </div>
              </Field>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Needed by (optional)">
                  <input
                    type="date"
                    value={form.dateNeeded}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, dateNeeded: e.target.value }))
                    }
                    className="w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base focus:border-tcs-border-input focus:outline-none"
                  />
                </Field>
                <Field label="Notes (optional)">
                  <textarea
                    rows={2}
                    value={form.notes}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, notes: e.target.value }))
                    }
                    placeholder="Anything the approver should know"
                    className="w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base focus:border-tcs-border-input focus:outline-none"
                  />
                </Field>
              </div>
            </div>

            {/* Lines */}
            <div className="mt-5 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
                  Items ({form.lines.length})
                </div>
                {anyLineDiffersFromEnvelope && (
                  <button
                    type="button"
                    onClick={applyEnvelopeUrgencyToAll}
                    className="text-xs text-tcs-accent-ink underline hover:no-underline"
                  >
                    Apply {form.urgencyDefault} to all lines
                  </button>
                )}
              </div>

              {form.lines.map((line, idx) => (
                <LineRow
                  key={line.id}
                  index={idx}
                  line={line}
                  canRemove={form.lines.length > 1}
                  envelopeUrgency={form.urgencyDefault}
                  onChange={(patch) => updateLine(line.id, patch)}
                  onRemove={() => removeLine(line.id)}
                />
              ))}

              <button
                type="button"
                onClick={addLine}
                className="w-full rounded-md border border-dashed border-tcs-border bg-tcs-raised px-3 py-2 text-sm font-medium text-tcs-accent-ink hover:bg-tcs-bg-muted"
              >
                + Add supply
              </button>
            </div>

            {!canConfirm && (
              <p className="mt-3 text-xs text-tcs-text-muted">
                {readyForConfirm()}
              </p>
            )}

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-tcs-border px-3 py-2 text-sm text-tcs-text-secondary hover:bg-tcs-bg-muted"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!canConfirm}
                onClick={() => setStep('confirm')}
                className="rounded-md bg-tcs-inverse px-4 py-2 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-50"
              >
                Review →
              </button>
            </div>
          </>
        )}

        {step === 'confirm' && (
          <ConfirmPanel
            form={form}
            estimatedTotal={estimatedTotal}
            onBack={() => setStep('capture')}
            submitting={submitting}
            error={error}
          />
        )}
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LineRow — one supply line. Picker + qty + unit + per-line urgency chip.
// ---------------------------------------------------------------------------

function LineRow({
  index,
  line,
  canRemove,
  envelopeUrgency,
  onChange,
  onRemove,
}: {
  index: number;
  line: LineState;
  canRemove: boolean;
  envelopeUrgency: SupplyUrgency;
  onChange: (patch: Partial<LineState>) => void;
  onRemove: () => void;
}) {
  const showOverrideChip = line.urgency !== envelopeUrgency;
  const cycleUrgency = () => {
    // Lightweight per-line override: tap the chip to cycle through tiers.
    const next: SupplyUrgency =
      line.urgency === 'Emergency'
        ? 'Soon'
        : line.urgency === 'Soon'
          ? 'Planned'
          : 'Emergency';
    onChange({ urgency: next });
  };

  return (
    <div className="rounded-md border border-tcs-border bg-tcs-raised p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-medium text-tcs-text-muted">
          Item #{index + 1}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={cycleUrgency}
            title="Tap to change urgency for this line"
            className={`rounded px-2 py-0.5 text-[10px] font-medium ${
              line.urgency === 'Emergency'
                ? 'bg-st-danger-bg-strong text-st-danger-fg-strong'
                : line.urgency === 'Soon'
                  ? 'bg-st-warn-bg-strong text-st-warn-fg-strong'
                  : 'bg-st-ok-bg-strong text-st-ok-fg-strong'
            } ${showOverrideChip ? 'ring-1 ring-offset-1 ring-tcs-border-input' : ''}`}
          >
            {line.urgency}
            {showOverrideChip ? ' • override' : ''}
          </button>
          {canRemove && (
            <button
              type="button"
              onClick={onRemove}
              aria-label={`Remove item ${index + 1}`}
              className="rounded-md border border-tcs-border bg-tcs-raised px-2 py-0.5 text-xs text-tcs-text-secondary hover:bg-st-danger-bg hover:text-st-danger-fg"
            >
              ×
            </button>
          )}
        </div>
      </div>

      <div className="mt-2">
        <SupplyPicker
          value={line.supply?.supply_id ?? null}
          onChange={(s) =>
            onChange({
              supply: s,
              itemDescription: s?.item_name ?? line.itemDescription,
              unit: s?.unit ?? line.unit,
            })
          }
        />
        {!line.supply && (
          <input
            value={line.itemDescription}
            onChange={(e) => onChange({ itemDescription: e.target.value })}
            placeholder="…or describe what you need (free text)"
            className="mt-2 w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base focus:border-tcs-border-input focus:outline-none"
          />
        )}
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2">
        <label className="block text-xs">
          <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">
            Quantity
          </span>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            value={line.quantity}
            onChange={(e) => onChange({ quantity: e.target.value })}
            placeholder="e.g. 1"
            className="w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base focus:border-tcs-border-input focus:outline-none"
          />
        </label>
        <label className="block text-xs">
          <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">
            Unit
          </span>
          <input
            value={line.unit}
            onChange={(e) => onChange({ unit: e.target.value })}
            placeholder="CASE / EACH / BOX"
            className="w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base uppercase focus:border-tcs-border-input focus:outline-none"
          />
        </label>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConfirmPanel — read-only review before submit. Each line gets its own group;
// estimated total at the bottom.
// ---------------------------------------------------------------------------

function ConfirmPanel({
  form,
  estimatedTotal,
  onBack,
  submitting,
  error,
}: {
  form: FormState;
  estimatedTotal: number | null;
  onBack: () => void;
  submitting: boolean;
  error: string | null;
}) {
  return (
    <div className="mt-4 space-y-4">
      {/* Envelope summary */}
      <div className="rounded-md border border-tcs-border bg-tcs-bg-muted p-4">
        <Row
          label="Facility"
          value={form.facility?.facility_name ?? '—'}
          sub={form.facility?.facility_address ?? undefined}
        />
        <Row
          label="Default urgency"
          value={form.urgencyDefault}
          tone={URGENCY_TONE[form.urgencyDefault]}
        />
        {form.dateNeeded && <Row label="Needed by" value={form.dateNeeded} />}
        {form.notes.trim() && <Row label="Notes" value={form.notes.trim()} />}
      </div>

      {/* Per-line summary */}
      <div className="space-y-2">
        {form.lines.map((line, idx) => {
          const itemLabel =
            line.supply?.item_name?.trim() || line.itemDescription.trim();
          const estCost = lineEstimatedCost(line);
          const status = deriveStatus(line.supply, line.urgency, estCost);
          const showStatusOverride = status !== 'Approved';
          return (
            <div
              key={line.id}
              className="rounded-md border border-tcs-border bg-tcs-bg-muted p-3"
            >
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium text-tcs-text-muted">
                  Item #{idx + 1}
                </div>
                <span
                  className={`rounded px-2 py-0.5 text-[10px] font-medium ${
                    line.urgency === 'Emergency'
                      ? 'bg-st-danger-bg-strong text-st-danger-fg-strong'
                      : line.urgency === 'Soon'
                        ? 'bg-st-warn-bg-strong text-st-warn-fg-strong'
                        : 'bg-st-ok-bg-strong text-st-ok-fg-strong'
                  }`}
                >
                  {line.urgency}
                </span>
              </div>
              <Row label="Item" value={itemLabel} />
              {line.supply?.supply_id && (
                <Row label="Catalog ID" value={line.supply.supply_id} mono />
              )}
              <Row
                label="Quantity"
                value={`${line.quantity} ${line.unit.toUpperCase()}`}
              />
              {estCost != null && (
                <Row label="Est. cost" value={`$${estCost.toFixed(2)}`} />
              )}
              <Row
                label="Initial status"
                value={status}
                tone={showStatusOverride ? 'amber' : 'green'}
              />
            </div>
          );
        })}
      </div>

      {estimatedTotal != null && (
        <div className="rounded-md border border-tcs-border bg-tcs-raised p-3 text-right">
          <div className="text-xs uppercase tracking-wide text-tcs-text-muted">
            Estimated total
          </div>
          <div className="mt-0.5 text-lg font-semibold text-tcs-text">
            ${estimatedTotal.toFixed(2)}
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-rag-red bg-st-danger-bg p-3 text-sm text-st-danger-fg-strong">
          {error}
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          disabled={submitting}
          className="rounded-md border border-tcs-border px-3 py-2 text-sm text-tcs-text-secondary hover:bg-tcs-bg-muted"
        >
          ← Edit
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-tcs-inverse px-5 py-2 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-50"
        >
          {submitting
            ? 'Submitting…'
            : `Submit ${form.lines.length} item${form.lines.length === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tiny presentational helpers
// ---------------------------------------------------------------------------

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">
        {label}
      </span>
      {children}
    </label>
  );
}

function Row({
  label,
  value,
  sub,
  tone,
  mono,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'red' | 'amber' | 'green';
  mono?: boolean;
}) {
  const toneClass =
    tone === 'red'
      ? 'bg-st-danger-bg-strong text-st-danger-fg-strong'
      : tone === 'amber'
        ? 'bg-st-warn-bg-strong text-st-warn-fg-strong'
        : tone === 'green'
          ? 'bg-st-ok-bg-strong text-st-ok-fg-strong'
          : '';
  return (
    <div className="grid grid-cols-3 gap-2 border-b border-tcs-border py-1.5 last:border-0">
      <div className="text-xs uppercase tracking-wide text-tcs-text-muted">
        {label}
      </div>
      <div className="col-span-2 text-sm text-tcs-text">
        {tone ? (
          <span
            className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${toneClass}`}
          >
            {value}
          </span>
        ) : (
          <span className={mono ? 'font-mono text-xs' : ''}>{value}</span>
        )}
        {sub && <div className="mt-0.5 text-xs text-tcs-text-muted">{sub}</div>}
      </div>
    </div>
  );
}
